import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { getWorkerPort, postToWorkerPort } from '@/util/workerPort';

/** How long a worker request waits for its reply before falling back. */
const WORKER_REPLY_TIMEOUT_MS = 3000;

/** Tuning for a single worker round-trip; every field is optional and defaults conservatively. */
export interface WorkerRequestOptions {
    /** Data to post alongside the request type. Defaults to null. */
    payload?: any;
    /**
     * Accepts or rejects a reply of the matching type. Needed when more than one request of the
     * same type can be in flight, so an earlier reply isn't mistaken for the current one.
     */
    matches?: (reply: any) => boolean;
    /** Overrides the default reply timeout for slower operations. */
    timeoutMs?: number;
}

@Component
export default class ClientServiceWorkerMixin extends Vue {
    private workerMessageHandler: ((e: MessageEvent) => void) | null = null;

    mounted() {
        const workerPort = getWorkerPort();
        if (!window.SharedWorker || !workerPort) return;

        this.workerMessageHandler = (e: MessageEvent) => this.onClientServiceWorkerMessage(e);
        workerPort.addEventListener('message', this.workerMessageHandler);
        this.onClientServiceWorkerReady(workerPort);
    }

    beforeDestroy() {
        const workerPort = getWorkerPort();
        if (this.workerMessageHandler && workerPort) {
            workerPort.removeEventListener('message', this.workerMessageHandler);
            this.workerMessageHandler = null;
        }
    }

    /**
     * Whether this tab has a worker port at all. Absent means the worker is not an option and the
     * caller has to use its own transport — it says nothing about whether the client service
     * behind the worker is reachable, which only a reply can establish.
     */
    protected get hasWorkerPort(): boolean {
        return !!this.$workerPort;
    }

    /**
     * Posts to the worker. Returns false when there is no port, so callers can fall back.
     *
     * A true return means only that the message was posted; the worker drops anything it can't
     * hand to the client service. Anything whose failure matters must use `requestFromWorker`
     * and act on the reply instead.
     */
    protected sendToWorker(type: string, data?: any): boolean {
        if (!getWorkerPort()) return false;

        postToWorkerPort(type, data ?? null);
        return true;
    }

    /**
     * Posts a request to the worker and resolves with its matching reply, or with `fallback`
     * if there is no port or the reply doesn't arrive in time. The listener and timer are
     * always torn down, so a silent worker can't accumulate either.
     */
    protected requestFromWorker<T>(requestType: string, replyType: string, fallback: T,
        options: WorkerRequestOptions = {}): Promise<T> {
        const workerPort = getWorkerPort();
        if (!workerPort) return Promise.resolve(fallback);

        return new Promise<T>(resolve => {
            const timeout = setTimeout(() => {
                workerPort.removeEventListener('message', handleMessage);
                resolve(fallback);
            }, options.timeoutMs ?? WORKER_REPLY_TIMEOUT_MS);

            const handleMessage = (e: MessageEvent) => {
                if (e.data[0] !== replyType) return;
                // An unmatched reply belongs to another in-flight request: ignore it and keep
                // waiting rather than resolving this one with someone else's outcome.
                if (options.matches && !options.matches(e.data[1])) return;
                clearTimeout(timeout);
                workerPort.removeEventListener('message', handleMessage);
                resolve(e.data[1] ?? fallback);
            };

            workerPort.addEventListener('message', handleMessage);
            workerPort.postMessage([requestType, options.payload ?? null]);
        });
    }

    protected onClientServiceWorkerMessage(_e: MessageEvent): void {}
    protected onClientServiceWorkerReady(_port: MessagePort): void {}
}
